import React, { createContext, useContext, useMemo } from 'react';

interface ShadowRootContextValue {
  container: HTMLElement | undefined;
  fullPageContainer?: HTMLElement | undefined;
}

const ShadowRootContext = createContext<ShadowRootContextValue>({
  container: undefined,
  fullPageContainer: undefined,
});

/**
 * Hook to access the shadow root container for portal rendering.
 *
 * When used inside a ShadowRootProvider, portals (dialogs, tooltips, etc.) will render
 * into the provided container element (typically a shadow root).
 *
 * When used outside a provider, returns undefined, causing Radix UI portals to fall back
 * to their default behavior of rendering into document.body.
 */
export const useShadowRoot = () => useContext(ShadowRootContext);

export const ShadowRootProvider: React.FC<{
  children: React.ReactNode;
  container: HTMLElement | null | undefined;
  fullPageContainer?: HTMLElement | null | undefined;
}> = ({ children, container, fullPageContainer }) => {
  const value = useMemo(
    () => ({
      container: container ?? undefined,
      fullPageContainer: fullPageContainer ?? undefined,
    }),
    [container, fullPageContainer],
  );

  return (
    <ShadowRootContext.Provider value={value}>
      {children}
    </ShadowRootContext.Provider>
  );
};
